import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useMotionValue, useDragControls } from 'framer-motion';
import {
  X,
  Minus,
  Plus,
  History,
  ArrowLeft,
  Archive,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useConversations,
  useConversation,
  useCreateConversation,
  useArchiveConversation,
  streamMessage,
  useAiStatus,
} from '../../../hooks/use-ai';
import type { Message } from '../../../hooks/use-ai';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { useQueryClient } from '@tanstack/react-query';
import { TruckyAvatar, type TruckyState } from './TruckyAvatar';

// ── Context detection ────────────────────────────────────────────────

type ContextType = 'general' | 'dispatch' | 'reports' | 'load_detail';

function usePageContext(): ContextType {
  const { pathname } = useLocation();

  if (pathname.startsWith('/dispatch-loads/') && !pathname.endsWith('/new')) return 'load_detail';
  if (pathname.startsWith('/dispatch')) return 'dispatch';
  if (pathname.startsWith('/reports')) return 'reports';
  return 'general';
}

// ── Loading dots ─────────────────────────────────────────────────────

function LoadingDots() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 mb-4">
      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
        {/* Match the regular AI message avatar size; pose driven so the
            "thinking" period before any content streams still looks alive. */}
        <TruckyAvatar state="working" className="h-full w-full p-0.5" />
      </div>
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="h-2 w-2 rounded-full bg-gray-400"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>
      <span className="text-xs text-gray-400">{t('components.chatPanel.thinking')}</span>
    </div>
  );
}

// ── Conversation Sidebar ─────────────────────────────────────────────

function ConversationSidebar({
  onSelect,
  onClose,
  activeId,
}: {
  onSelect: (id: string) => void;
  onClose: () => void;
  activeId: string | null;
}) {
  const { t } = useTranslation();
  const { data: conversations, isLoading } = useConversations();
  const archiveMutation = useArchiveConversation();

  return (
    <motion.div
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      exit={{ x: -280 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="absolute inset-0 z-10 bg-white flex flex-col rounded-2xl"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">{t('components.chatPanel.history')}</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-gray-400">
            {t('common.loading')}
          </div>
        ) : !conversations?.length ? (
          <div className="p-4 text-center text-sm text-gray-400">
            {t('components.chatPanel.noConversations')}
          </div>
        ) : (
          conversations
            .filter((c) => !c.archived)
            .map((conv) => (
              <div
                key={conv.id}
                className={clsx(
                  'flex items-center justify-between px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors',
                  activeId === conv.id && 'bg-gray-50'
                )}
                onClick={() => onSelect(conv.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {conv.title || t('components.chatPanel.newConversation')}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {new Date(conv.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    archiveMutation.mutate(conv.id);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors ml-2"
                  title={t('components.chatPanel.archive')}
                >
                  <Archive className="h-3 w-3 text-gray-400" />
                </button>
              </div>
            ))
        )}
      </div>
    </motion.div>
  );
}

// ── Draggable position ───────────────────────────────────────────────

// Persisted offset (in px) from the default bottom-right anchor. Trucky
// otherwise sits on top of the action buttons that live in the bottom-right
// of most screens (e.g. "Save changes"), so we let the user drag him out of
// the way and remember where they parked him.
const TRUCKY_POS_KEY = 'trucky-launcher-offset';

type Offset = { x: number; y: number };

function readStoredOffset(): Offset {
  try {
    const raw = window.localStorage.getItem(TRUCKY_POS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Offset>;
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        return { x: parsed.x, y: parsed.y };
      }
    }
  } catch {
    // ignore malformed/unavailable storage — fall back to the default anchor
  }
  return { x: 0, y: 0 };
}

function writeStoredOffset(offset: Offset) {
  try {
    window.localStorage.setItem(TRUCKY_POS_KEY, JSON.stringify(offset));
  } catch {
    // storage may be unavailable (private mode / quota) — position just
    // won't persist across reloads, which is acceptable.
  }
}

const clampToRange = (v: number, a: number, b: number) =>
  Math.min(Math.max(v, Math.min(a, b)), Math.max(a, b));

const MARGIN = 8; // clearance from the viewport edges
// The app chrome is a fixed header (50px) stacked over a fixed nav (46px),
// both at a much higher z-index than Trucky. Keep Trucky below them so he
// can never be dragged into — and hidden behind — that top bar.
const TOP_SAFE = 96 + MARGIN;

// The launcher is a tiny pill but the panel is ~400×600. Applying the pill's
// raw drag offset to the panel would shove its header off-screen, so we clamp
// the panel's offset to keep the whole panel (header included) inside the
// viewport, below the top chrome. Both are anchored bottom-right
// (right-6 / bottom≈40px).
function clampPanelOffset(raw: Offset): Offset {
  if (typeof window === 'undefined') return raw;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const RIGHT_GAP = 24; // right-6
  const BOTTOM_GAP = 40; // calc(24px + 1rem)
  const panelW = Math.min(400, vw - 32);
  const panelH = Math.min(600, vh - TOP_SAFE - MARGIN);
  const defaultLeft = vw - RIGHT_GAP - panelW;
  const defaultTop = vh - BOTTOM_GAP - panelH;
  return {
    x: clampToRange(raw.x, MARGIN - defaultLeft, vw - MARGIN - panelW - defaultLeft),
    y: clampToRange(raw.y, TOP_SAFE - defaultTop, vh - MARGIN - panelH - defaultTop),
  };
}

// ── Main ChatPanel ───────────────────────────────────────────────────

export function ChatPanel() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [truckyState, setTruckyState] = useState<TruckyState>('normal');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const contextType = usePageContext();
  const createConversation = useCreateConversation();
  const { data: aiStatus } = useAiStatus();

  // ── Drag-to-move ────────────────────────────────────────────────────
  // Shared drag offset for both the floating launcher and the open panel,
  // so wherever the user parks Trucky is where he re-opens. Persisted to
  // localStorage. `dragConstraintsRef` is a full-viewport box that keeps him
  // on screen; `dragControls` lets the panel be dragged by its header only.
  const initialOffset = useRef<Offset>(readStoredOffset());
  // Launcher pill offset — persisted; this is Trucky's saved "home".
  const x = useMotionValue(initialOffset.current.x);
  const y = useMotionValue(initialOffset.current.y);
  // Panel offset — derived (clamped) from the launcher on open so the whole
  // panel stays on-screen, then freely draggable for the session.
  const panelX = useMotionValue(0);
  const panelY = useMotionValue(0);
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelDragControls = useDragControls();
  // Distinguishes a drag from a click so releasing after a drag doesn't
  // also open the chat.
  const didDragRef = useRef(false);

  // Clamp the launcher into the visible, header-clear region and persist.
  // Measures the pill's actual box so it works regardless of label width; the
  // `defaultLeft/Top` are recovered by subtracting the currently applied
  // transform. Corrects a previously-saved off-screen position (e.g. dragged
  // up under the fixed top bar) and keeps him reachable after a resize.
  const clampLauncher = useCallback(() => {
    const el = launcherRef.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return; // mid entrance animation
    const curX = x.get();
    const curY = y.get();
    const defaultLeft = rect.left - curX;
    const defaultTop = rect.top - curY;
    const nx = clampToRange(
      curX,
      MARGIN - defaultLeft,
      window.innerWidth - MARGIN - rect.width - defaultLeft,
    );
    const ny = clampToRange(
      curY,
      TOP_SAFE - defaultTop,
      window.innerHeight - MARGIN - rect.height - defaultTop,
    );
    if (nx !== curX || ny !== curY) {
      x.set(nx);
      y.set(ny);
    }
    writeStoredOffset({ x: nx, y: ny });
  }, [x, y]);

  // Correct the restored position once the pill has laid out (after its
  // entrance animation), and again on resize.
  useEffect(() => {
    if (isOpen) return;
    const t = window.setTimeout(clampLauncher, 350);
    window.addEventListener('resize', clampLauncher);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', clampLauncher);
    };
  }, [isOpen, clampLauncher]);

  // When the panel opens, place it near the launcher but clamped fully into
  // the viewport (so the header — the drag handle — is always reachable).
  // Re-clamp on resize so it can't drift off-screen.
  useEffect(() => {
    if (!isOpen) return;
    const apply = () => {
      const clamped = clampPanelOffset({ x: x.get(), y: y.get() });
      panelX.set(clamped.x);
      panelY.set(clamped.y);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [isOpen, x, y, panelX, panelY]);

  // Trucky animation state machine:
  // - When chat opens, wave for ~2.5s then settle to normal.
  // - While streaming, swap to working (TruckyAvatar self-alternates
  //   between working/normal to feel animated).
  // - When streaming finishes, celebrate with success for ~1.8s then
  //   return to normal.
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    setTruckyState('wave');
    const t = window.setTimeout(() => setTruckyState('normal'), 2500);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    if (isStreaming) {
      setTruckyState('working');
      prevStreamingRef.current = true;
    } else if (prevStreamingRef.current) {
      prevStreamingRef.current = false;
      setTruckyState('success');
      const t = window.setTimeout(() => setTruckyState('normal'), 1800);
      cleanup = () => window.clearTimeout(t);
    }
    return cleanup;
  }, [isStreaming]);

  const { data: conversationData } = useConversation(conversationId ?? '');
  const messages = conversationData?.messages ?? localMessages;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const handleNewConversation = useCallback(async () => {
    setConversationId(null);
    setLocalMessages([]);
    setStreamingContent('');
    setShowHistory(false);
  }, []);

  const handleSend = useCallback(
    async (content: string) => {
      let convId = conversationId;

      // Create a new conversation if needed
      if (!convId) {
        try {
          const conv = await createConversation.mutateAsync({
            title: content.slice(0, 60),
            contextType,
          });
          convId = conv.id;
          setConversationId(convId);
        } catch {
          // Fallback: AI unavailable — show a friendly error instead of a stub
          const userMsg: Message = {
            id: `local-${Date.now()}`,
            conversationId: 'local',
            role: 'user',
            content,
            createdAt: new Date().toISOString(),
          };
          const errMsg: Message = {
            id: `local-${Date.now()}-error`,
            conversationId: 'local',
            role: 'assistant',
            content: t('components.chatPanel.unavailable'),
            createdAt: new Date().toISOString(),
          };
          setLocalMessages((prev) => [...prev, userMsg, errMsg]);
          return;
        }
      }

      // Add user message optimistically
      const userMsg: Message = {
        id: `temp-${Date.now()}`,
        conversationId: convId,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };

      // Update local cache optimistically
      queryClient.setQueryData(
        ['ai', 'conversations', convId],
        (old: { messages: Message[] } | undefined) => ({
          ...old,
          messages: [...(old?.messages ?? []), userMsg],
        })
      );

      setIsStreaming(true);
      setStreamingContent('');

      streamMessage(
        convId,
        content,
        contextType,
        (chunk) => {
          setStreamingContent((prev) => prev + chunk);
        },
        () => {
          setIsStreaming(false);
          setStreamingContent('');
          queryClient.invalidateQueries({
            queryKey: ['ai', 'conversations', convId],
          });
        },
        () => {
          setIsStreaming(false);
          setStreamingContent('');
          // On error: invalidate to reload the saved response (or error message)
          queryClient.invalidateQueries({
            queryKey: ['ai', 'conversations', convId],
          });
        }
      );
    },
    [conversationId, contextType, createConversation, queryClient, t]
  );

  const handleSelectConversation = (id: string) => {
    setConversationId(id);
    setShowHistory(false);
    setLocalMessages([]);
    setStreamingContent('');
  };

  return (
    <>
      {/* Click-through box that bounds all Trucky dragging so he can never be
          dropped off-screen or up behind the fixed top header/nav (which sit
          at a far higher z-index). Its top starts below that chrome. */}
      <div
        ref={dragConstraintsRef}
        className="fixed left-0 right-0 bottom-0 z-40 pointer-events-none"
        style={{ top: TOP_SAFE }}
        aria-hidden="true"
      />

      {/* Floating Button — light "white smoke" pill so Trucky reads
          against the dark dispatch background without color-contrast
          issues, with a label so it's clear what the button does.
          Drag the pill anywhere to move Trucky out from over the
          bottom-right action buttons; his position is remembered.
          A continuous gentle y-bob (≈3s loop, on the inner content so it
          doesn't fight the drag transform) makes the pill feel alive before
          the user has summoned it; Trucky inside waves on his own cadence
          via `idleWave`. */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            ref={launcherRef}
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => {
              didDragRef.current = true;
            }}
            onDragEnd={() => {
              clampLauncher();
              // The click that trails a drag fires before this macrotask, so
              // it gets swallowed below; if no trailing click fires, this
              // still clears the flag so the next real click opens the chat.
              window.setTimeout(() => {
                didDragRef.current = false;
              }, 0);
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              // A drag ends with a click event too — swallow it so dragging
              // Trucky doesn't also pop the chat open.
              if (didDragRef.current) {
                didDragRef.current = false;
                return;
              }
              setIsOpen(true);
              setIsMinimized(false);
            }}
            className="fixed right-6 z-40 flex items-center gap-2.5 h-14 pl-1.5 pr-4 rounded-full bg-[#F5F5F7] text-[#1B3050] ring-1 ring-black/5 shadow-[0_10px_24px_-6px_rgba(13,30,51,0.35)] hover:shadow-[0_14px_28px_-6px_rgba(13,30,51,0.45)] transition-shadow cursor-grab active:cursor-grabbing touch-none"
            style={{ x, y, bottom: 'calc(24px + 1rem)' /* clear the brand footer */ }}
            aria-label={t('components.chatPanel.openAria')}
          >
            <motion.span
              className="flex items-center gap-2.5"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            >
              <TruckyAvatar state="normal" idleWave className="h-11 w-11" />
              <span className="text-sm font-semibold tracking-tight">{t('components.chatPanel.assistantName')}</span>
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragListener={false}
            dragControls={panelDragControls}
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            // No `y` in the entrance animation: `y` is a drag motion value
            // bound via `style` below, so animating it here would snap the
            // panel back to the default anchor on every open.
            initial={{ opacity: 0, scale: 0.95 }}
            animate={
              isMinimized
                ? { opacity: 1, scale: 1, height: 56 }
                : { opacity: 1, scale: 1, height: 'auto' }
            }
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={clsx(
              'fixed right-6 z-40',
              'w-[400px] max-w-[calc(100vw-2rem)]',
              'bg-white rounded-2xl shadow-2xl border border-gray-200',
              'flex flex-col overflow-hidden',
              !isMinimized && 'max-h-[600px] h-[600px]'
            )}
            style={{ x: panelX, y: panelY, bottom: 'calc(24px + 1rem)' /* clear the brand footer */ }}
          >
            {/* Header — doubles as a drag handle (title area only, so the
                action buttons on the right stay clickable). */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#1B3050] text-white rounded-t-2xl flex-shrink-0">
              <div
                className="flex items-center gap-2 min-w-0 flex-1 cursor-grab active:cursor-grabbing select-none touch-none"
                onPointerDown={(e) => panelDragControls.start(e)}
                title={t('components.chatPanel.dragToMove')}
              >
                <span className="font-semibold text-sm whitespace-nowrap">
                  {t('components.chatPanel.assistantName')}
                </span>
                {aiStatus?.available === false && (
                  <span
                    className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] bg-white/10 rounded-full px-2 py-0.5 text-white/60"
                    title={t('components.chatPanel.offlineTitle')}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400 inline-block" />
                    {t('components.chatPanel.offline')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="p-1.5 hover:bg-white/15 rounded-lg transition-colors"
                  title={t('components.chatPanel.historyTitle')}
                >
                  <History className="h-4 w-4" />
                </button>
                <button
                  onClick={handleNewConversation}
                  className="p-1.5 hover:bg-white/15 rounded-lg transition-colors"
                  title={t('components.chatPanel.newConversation')}
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/15 rounded-lg transition-colors"
                  title={isMinimized ? t('components.chatPanel.expand') : t('components.chatPanel.minimize')}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-white/15 rounded-lg transition-colors"
                  title={t('components.chatPanel.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            {!isMinimized && (
              <>
                <div className="relative flex-1 flex flex-col" style={{ minHeight: 0, overflow: 'hidden' }}>
                  {/* Conversation History Sidebar */}
                  <AnimatePresence>
                    {showHistory && (
                      <ConversationSidebar
                        onSelect={handleSelectConversation}
                        onClose={() => setShowHistory(false)}
                        activeId={conversationId}
                      />
                    )}
                  </AnimatePresence>

                  {/* Messages */}
                  <div
                    className="px-4 py-4 flex-1"
                    style={{
                      overflowY: 'auto',
                      minHeight: 0,
                      overscrollBehavior: 'contain',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    {messages.length === 0 && !streamingContent ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        {/* Big idle Trucky — waves periodically AND a slow
                            CSS sway so the empty state never looks frozen
                            before the user has started a conversation. */}
                        <motion.div
                          animate={{ y: [0, -4, 0] }}
                          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
                          className="mb-3"
                        >
                          <TruckyAvatar
                            state={truckyState}
                            idleWave
                            className="h-28 w-28"
                          />
                        </motion.div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">
                          {t('components.chatPanel.greeting')}
                        </h4>
                        <p className="text-xs text-gray-500 max-w-[240px]">
                          {t('components.chatPanel.intro')}
                        </p>
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                          {[
                            {
                              label: t('components.chatPanel.suggestOptimizeDispatch'),
                              prompt: t('components.chatPanel.suggestOptimizeDispatch'),
                            },
                            {
                              label: t('components.chatPanel.suggestDriverAvailability'),
                              prompt: t('components.chatPanel.suggestDriverAvailability'),
                            },
                            {
                              label: t('components.chatPanel.suggestRevenueSummary'),
                              prompt: t('components.chatPanel.suggestRevenueSummary'),
                            },
                          ].map((suggestion) => (
                            <button
                              key={suggestion.label}
                              onClick={() => handleSend(suggestion.prompt)}
                              className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                            >
                              {suggestion.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        {messages.map((msg) => (
                          <ChatMessage key={msg.id} message={msg} />
                        ))}
                        {/* Streaming message — animate Trucky's avatar
                            in lockstep with the global truckyState so he
                            visibly works while the response composes. */}
                        {streamingContent && (
                          <ChatMessage
                            truckyState={truckyState}
                            message={{
                              id: 'streaming',
                              conversationId: conversationId ?? 'local',
                              role: 'assistant',
                              content: streamingContent,
                              createdAt: new Date().toISOString(),
                            }}
                          />
                        )}
                        {isStreaming && !streamingContent && <LoadingDots />}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>
                </div>

                {/* Input */}
                <ChatInput onSend={handleSend} disabled={isStreaming} />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
